/**
 * API Client for Job Widget SDK
 */

import type {
  WidgetConfig,
  Job,
  Application,
  User,
  APIResponse,
  PaginatedResponse,
  WidgetError,
  AuthToken,
  ProfileIncompleteData
} from './types';
import { Logger } from './logger';

export class APIClient {
  private config: WidgetConfig;
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(config: WidgetConfig) {
    this.config = config;

    this.baseUrl = config.baseUrl || '';

    this.headers = {
      'Content-Type': 'application/json'
    };

    if (config.apiKey && config.tenantId) {
      this.headers['X-API-Key'] = config.apiKey;
      this.headers['X-Tenant-ID'] = config.tenantId;
    }

    // Initialize logger for API client
    Logger.init({
      debug: config.debug || false,
      prefix: 'APIClient'
    });
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const token = this.getStoredToken();
      const headers: Record<string, string> = {
        ...this.headers,
        ...(options.headers as Record<string, string> || {})
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token.access_token}`;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific status codes
        if (response.status === 401) {
          this.clearStoredToken();
          throw {
            code: 'AUTH_REQUIRED',
            message: 'Authentication required',
            details: data
          } as WidgetError;
        }

        if (response.status === 428) {
          throw {
            code: 'PROFILE_INCOMPLETE',
            message: data.message || 'Profile incomplete',
            details: data
          } as WidgetError;
        }

        if (response.status === 409) {
          return {
            success: false,
            error: {
              code: 'ALREADY_APPLIED',
              message: data.message || 'Already applied to this job',
              details: data
            }
          };
        }

        throw {
          code: 'API_ERROR',
          message: data.message || 'API request failed',
          details: data
        } as WidgetError;
      }

      // Check if response already has success/data structure
      if (data.success !== undefined && data.data !== undefined) {
        return data; // Already in correct format
      }

      return {
        success: true,
        data: data
      };
    } catch (error: any) {
      if (error.code) {
        throw error;
      }

      throw {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network request failed',
        details: error
      } as WidgetError;
    }
  }

  /**
   * Get stored authentication token
   */
  private getStoredToken(): AuthToken | null {
    const tokenStr = sessionStorage.getItem('job_widget_token');
    if (!tokenStr) return null;

    try {
      const token = JSON.parse(tokenStr) as AuthToken;
      if (token.expires_at > Date.now()) {
        return token;
      }
      this.clearStoredToken();
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Store authentication token
   */
  private storeToken(token: AuthToken): void {
    sessionStorage.setItem('job_widget_token', JSON.stringify(token));
  }

  /**
   * Clear stored token
   */
  private clearStoredToken(): void {
    sessionStorage.removeItem('job_widget_token');
    sessionStorage.removeItem('job_widget_state');
  }

  /**
   * Exchange OAuth code for token
   */
  async exchangeCodeForToken(code: string, _state: string): Promise<AuthToken> {
    const response = await fetch(`${this.config.baseUrl}/auth/v1/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        client_id: this.getOAuthClientId(),
        redirect_uri: this.getOAuthRedirectUri()
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw {
        code: 'AUTH_FAILED',
        message: error.error_description || 'Authentication failed',
        details: error
      } as WidgetError;
    }

    const data = await response.json();
    const token: AuthToken = {
      access_token: data.access_token,
      expires_at: Date.now() + (data.expires_in * 1000),
      scope: data.scope?.split(' ') || ['profile:basic', 'email']
    };

    this.storeToken(token);
    return token;
  }

  /**
   * Get OAuth client ID
   */
  private getOAuthClientId(): string {
    return this.config.clientId || '';
  }

  /**
   * Get OAuth redirect URI
   */
  private getOAuthRedirectUri(): string {
    return this.config.redirectUri || `${window.location.origin}/oauth-callback`;
  }

  /**
   * Get list of jobs
   */
  async getJobs(params?: {
    page?: number;
    per_page?: number;
    department?: string;
    location?: string;
    search?: string;
  }): Promise<PaginatedResponse<Job>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }

    const response = await this.request<PaginatedResponse<Job>>(
      `/v1/jobs${queryParams.toString() ? `?${queryParams}` : ''}`
    );

    if (!response.success) {
      throw response.error;
    }

    return response.data!;
  }

  /**
   * Get single job details
   */
  async getJob(jobId: string): Promise<Job> {
    const response = await this.request<Job>(`/v1/jobs/${jobId}`);

    if (!response.success) {
      throw response.error;
    }

    return response.data!;
  }

  /**
   * Submit job application
   */
  async applyToJob(jobId: string, tenantId: string, data?: {
    cover_letter?: string;
    resume_id?: string;
    answers?: Record<string, any>;
  }): Promise<Application | ProfileIncompleteData | { status: string; message: string }> {
    const token = this.getStoredToken();

    if (!token || token.expires_at < Date.now()) {
      this.clearStoredToken();
      throw {
        code: 'AUTH_REQUIRED',
        message: 'Authentication required',
        details: null
      } as WidgetError;
    }

    const endpoint = `/v1/jobs/${jobId}/applications`;
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token.access_token}`,
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data || {})
      });

      if (response.status === 401) {
        this.clearStoredToken();
        throw {
          code: 'AUTH_EXPIRED',
          message: 'Token expired, re-authentication required',
          details: null
        } as WidgetError;
      }

      if (response.status === 428) {
        const error = await response.json();

        sessionStorage.setItem('job_widget_pending_application', JSON.stringify({
          jobId,
          tenantId,
          additionalData: data,
          returnUrl: window.location.href
        }));

        return {
          profile_completeness: error.profile_completeness || 0,
          minimum_required: error.minimum_required || 50,
          profile_url: error.profile_url,
          missing_sections: error.missing_sections || [],
          message: error.message || 'Please complete your profile to apply'
        } as ProfileIncompleteData;
      }

      if (response.status === 409) {
        const error = await response.json();
        return {
          status: 'already_applied',
          message: error.message || error.detail || 'You have already applied to this job'
        };
      }

      if (!response.ok) {
        const error = await response.json();
        throw {
          code: 'API_ERROR',
          message: error.message || error.detail || 'Failed to submit application',
          details: error
        } as WidgetError;
      }

      const result = await response.json();
      return result as Application;

    } catch (error) {
      if ((error as WidgetError).code) {
        throw error;
      }
      throw {
        code: 'NETWORK_ERROR',
        message: `Failed to apply: ${error}`,
        details: error
      } as WidgetError;
    }
  }

  /**
   * Get user profile
   */
  async getUserProfile(): Promise<User> {
    const response = await this.request<User>('/v1/user/profile');

    if (!response.success) {
      throw response.error;
    }

    return response.data!;
  }

  /**
   * Get application status
   */
  async getApplicationStatus(applicationId: string): Promise<Application> {
    const response = await this.request<Application>(`/v1/applications/${applicationId}`);

    if (!response.success) {
      throw response.error;
    }

    return response.data!;
  }

  /**
   * Get user's applications
   */
  async getUserApplications(): Promise<Application[]> {
    const response = await this.request<Application[]>('/v1/user/applications');

    if (!response.success) {
      throw response.error;
    }

    return response.data!;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getStoredToken() !== null;
  }

  /**
   * Get OAuth authorization URL
   */
  getOAuthUrl(state: string): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.getOAuthClientId(),
      redirect_uri: this.getOAuthRedirectUri(),
      scope: 'profile:basic email',
      state: state
    });

    return `${this.config.baseUrl}/auth/v1/oauth/authorize?${params}`;
  }

  /**
   * Clear all stored data
   */
  clearStorage(): void {
    this.clearStoredToken();
    sessionStorage.removeItem('job_widget_applied_jobs');
    sessionStorage.removeItem('job_widget_state');
  }
}